package org.multipaz.wallet.web

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import org.multipaz.wallet.shared.BuildConfig
import org.multipaz.wallet.shared.ClientType
import org.multipaz.util.Platform
import org.multipaz.wallet.client.WalletClient
import org.multipaz.wallet.client.clearOnSignOut
import io.ktor.client.engine.js.Js
import org.multipaz.document.buildDocumentStore
import org.multipaz.documenttype.DocumentTypeRepository
import org.multipaz.documenttype.knowntypes.addKnownTypes
import org.multipaz.securearea.SecureAreaRepository
import org.multipaz.util.Logger
import react.create
import react.dom.client.createRoot
import web.dom.Element

private const val TAG = "Main"

fun main() {
    js("require('./style.css')")
    window.onload = {
        CoroutineScope(Dispatchers.Main).launch {
            try {
                val storage = Platform.storage
                val secureArea = Platform.getSecureArea(storage)
                val backendUrl = window.location.origin
                
                val walletClient = WalletClient.create(
                    clientType = ClientType.WEB,
                    url = backendUrl,
                    secret = BuildConfig.BACKEND_SECRET,
                    storage = storage,
                    secureArea = secureArea,
                    httpClientEngineFactory = Js,
                    numReaderKeys = 0,
                    clientDevice = null,
                    clientPlatform = getWebClientPlatform()
                )

                val documentTypeRepository = DocumentTypeRepository()
                documentTypeRepository.addKnownTypes()
                
                val googleSignIn = GoogleSignIn(BuildConfig.BACKEND_CLIENT_ID)
                googleSignIn.initialize()

                val secureAreaRepository = SecureAreaRepository.Builder()
                    .add(secureArea)
                    .build()

                val documentStore = buildDocumentStore(
                    storage = storage,
                    secureAreaRepository = secureAreaRepository
                ) {}
                documentStore.clearOnSignOut(walletClient, CoroutineScope(Dispatchers.Main))

                val documentModel = DocumentModel.create(
                    documentStore = documentStore,
                    documentTypeRepository = documentTypeRepository
                )

                val settingsModel = SettingsModel.create()

                val isVerifyPage = window.location.pathname.startsWith("/web/verify") || 
                        window.location.pathname.startsWith("/verify")
                val isKeysPage = window.location.pathname.startsWith("/web/keys") || 
                        window.location.pathname.startsWith("/keys")
                val isDocPage = window.location.pathname.contains("/privacy") ||
                        window.location.pathname.contains("/terms") ||
                        window.location.pathname.contains("/google-privacy") ||
                        window.location.pathname.contains("/google-terms")

                val rootElement = document.getElementById("root") ?: error("No root element found")
                val root = createRoot(rootElement.unsafeCast<Element>())
                if (isVerifyPage) {
                    root.render(VerifyApp.create {
                        this.walletClient = walletClient
                    })
                } else if (isKeysPage) {
                    root.render(KeysApp.create())
                } else if (isDocPage) {
                    root.render(DocViewerApp.create())
                } else {
                    root.render(App.create {
                        this.walletClient = walletClient
                        this.googleSignIn = googleSignIn
                        this.documentTypeRepository = documentTypeRepository
                        this.documentStore = documentStore
                        this.documentModel = documentModel
                        this.settingsModel = settingsModel
                    })
                }
                
            } catch (e: Exception) {
                Logger.e(TAG, "Failed to initialize application", e)
                val root = document.getElementById("root")
                if (root != null) {
                    root.innerHTML = "<h1>Error initializing application</h1><p>${e.message}</p>"
                }
            }
        }
    }
}

private fun getWebClientPlatform(): String {
    val ua = window.navigator.userAgent
    return when {
        ua.contains("Edg/") -> {
            val version = ua.substringAfter("Edg/").substringBefore(".").substringBefore(" ")
            "Microsoft Edge $version"
        }
        ua.contains("OPR/") -> {
            val version = ua.substringAfter("OPR/").substringBefore(".").substringBefore(" ")
            "Opera $version"
        }
        ua.contains("CriOS/") -> {
            val version = ua.substringAfter("CriOS/").substringBefore(".").substringBefore(" ")
            "Google Chrome $version"
        }
        ua.contains("Chrome/") -> {
            val version = ua.substringAfter("Chrome/").substringBefore(".").substringBefore(" ")
            "Google Chrome $version"
        }
        ua.contains("FxiOS/") -> {
            val version = ua.substringAfter("FxiOS/").substringBefore(".").substringBefore(" ")
            "Firefox $version"
        }
        ua.contains("Firefox/") -> {
            val version = ua.substringAfter("Firefox/").substringBefore(".").substringBefore(" ")
            "Firefox $version"
        }
        ua.contains("Safari/") && ua.contains("Version/") -> {
            val version = ua.substringAfter("Version/").substringBefore(".").substringBefore(" ")
            "Safari $version"
        }
        else -> "Web Browser"
    }
}
